import { Button, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useUser } from "../../context/userContext";
import { updateSalesByAdmin } from "../../api/salesApi";
import { createLog } from "../../api/logApi";
import { toNullIfEmptyOrZero } from "../../utils/priceUtils";
import handleAgentName from "../../utils/userUtils";
import GenericSalesForm from "../common/genericSalesForm";
import { getAllProducts } from "../../api/productApi";
import { convertDateStringToDateTime, convertDateTimeToDateString } from "../../utils/datetimeUtils";

export default function EditAdminSalesModal({ show, onHide, selectedSale, onSaved }) {
    const { user } = useUser();
    const isSuperAdmin = user?.roles.includes("Super Admin");
    const isAdmin = user?.roles.includes("Admin");

    const canEditOriPrice = isAdmin || isSuperAdmin;

    const availablePaymentMethods = [
        { label: "Cash", value: "cash" },
        { label: "Credit Card 24 Months", value: "creditCard24" },
        { label: "Credit Card 36 Months", value: "creditCard36" },
        { label: "Credit Card 60 Months", value: "creditCard60" }
    ];

    const [originalPrice, setOriginalPrice] = useState();
    const [finalPrice, setFinalPrice] = useState();
    const [asternDiscount, setAsternDiscount] = useState();
    const [agentDiscount, setAgentDiscount] = useState();

    const [customerName, setCustomerName] = useState();
    const [customerEmail, setCustomerEmail] = useState();
    const [customerPhone, setCustomerPhone] = useState();
    const [customerAddress, setCustomerAddress] = useState();

    // New State for Product/Category Logic
    const [productList, setProductList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);
    const [residentialMatch, setResidentialMatch] = useState(null);
    const [canEditPrice, setCanEditPrice] = useState(false);
    // const [productOptions, setProductOptions] = useState([]);
    // const [selectedProductId, setSelectedProductId] = useState();
    const [paymentMethod, setPaymentMethod] = useState();

    const [depositOn, setDepositOn] = useState();

    const fetchProducts = async () => {
        try {
            const response = await getAllProducts();
            if (response.status === 200) {
                setProductList(response.data);
            }
            else {
                console.error("Unexpected response:", response);
            }
        }
        catch (error) {
            console.error("Error fetching products:", error);
        }
    }

    useEffect(() => {
        if (show && selectedSale) {
            setOriginalPrice(selectedSale.originalPrice);
            setFinalPrice(selectedSale.finalPrice);
            setAsternDiscount(selectedSale.discountByAstern);
            setAgentDiscount(selectedSale.discountByAgent);

            setCustomerName(selectedSale.customerDetails?.customerName ?? "");
            setCustomerEmail(selectedSale.customerDetails?.email ?? "");
            setCustomerPhone(selectedSale.customerDetails?.phoneNumber ?? "");
            setCustomerAddress(selectedSale.customerDetails?.address ?? "");

            setSelectedCategory(selectedSale.productCategory);
            setPanelQty(selectedSale.panelQty || 0);
            setEnergy(selectedSale.energy || 0);

            if (selectedSale.productCategory === "RESIDENTIAL") {
                // For Residential, we may want to auto-match product later
                setResidentialMatch(true); // Reset first
            }
            else {
                setResidentialMatch(); // Not applicable
            }

            // If superadmin, fetch all products
            if (isSuperAdmin) {
                fetchProducts();
            } else {
                setProductList([]); // Not editable for non-superadmin
            }
            setPaymentMethod(selectedSale.paymentMethod ?? "");
            setProductList([]);
            setDepositOn(selectedSale.depositOn ?? "");
        }
        else if (!show && !selectedSale) {
            setOriginalPrice();
            setFinalPrice();
            setAsternDiscount();
            setAgentDiscount();

            setCustomerName();
            setCustomerEmail();
            setCustomerPhone();
            setCustomerAddress();

            setPaymentMethod();
            setProductList([]);

            setDepositOn();
        }
    }, [show, selectedSale, isSuperAdmin]);

    // ===== Calculate Energy (kWp) =====
    // Only calculate if panelQty changes and we are editing (not just initial load)
    // However, for simplicity in this modal, we might want to trust the user input or auto-calc
    // mirroring createSalesForm:
    // Note: In createSalesForm, energy is set when panelQty changes. 
    // Here we might need to be careful not to overwrite existing energy if it was custom?
    // But usually energy = panelQty * 0.62.

    // ===== Set Price Editing Permission Based on Category =====
    useEffect(() => {
        // Commercial can edit price, Residential cannot (unless super admin overrides? logic says canEditOriPrice is isAdmin || isSuperAdmin)
        // But we also want to follow the form logic:
        const isCommercial = selectedCategory === "COMMERCIAL";
        // If user is admin/superadmin, they can generally edit, but for Residential we want auto-populating.
        // Let's stick to the form logic: Residential = auto, Commercial = manual.
        // But since this is an ADMIN edit modal, maybe we allow override?
        // The original code had `canEditOriPrice = isAdmin || isSuperAdmin`.
        // We should probably respect that, but also provide the auto-match feature.
        setCanEditPrice(isCommercial || canEditOriPrice);
    }, [selectedCategory, canEditOriPrice]);

    // ===== RESIDENTIAL LOGIC: Auto-populate Price =====
    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && panelQty > 0 && isSuperAdmin) {
            // Only run this logic if SuperAdmin (who can edit product details)

            const match = productList.find(p =>
                p.productCategory === "RESIDENTIAL" &&
                p.panelQty === panelQty &&
                p.energy === energy
            );
            setResidentialMatch(match);

            // Auto-update price if match found AND payment method selected
            // AND we want to enforce standard pricing (maybe optional in edit?)
            // For now, let's replicate create logic: it updates the price.
            if (match && paymentMethod) {
                const price = match[paymentMethod];
                setOriginalPrice(price || 0);
            }
        }
    }, [selectedCategory, panelQty, energy, paymentMethod, productList, isSuperAdmin]);

    useEffect(() => {
        const oriPrice = originalPrice || 0;
        const asternDisc = asternDiscount || 0;
        const agentDisc = agentDiscount || 0;
        const afterDisc = oriPrice - asternDisc - agentDisc;

        setFinalPrice(Math.max(0, afterDisc));

    }, [originalPrice, asternDiscount, agentDiscount]);

    // const handleProductOptionsChange = (e) => {
    //     const productId = e.target.value;
    //     setSelectedProductId(productId);
    //     const selectedProduct = productOptions.find(p => String(p.productId) === String(productId));
    //     setOriginalPrice(selectedProduct?.price ?? 0);

    //     if (selectedProduct) {
    //         setPaymentMethod(selectedProduct.paymentMethod);
    //     }
    //     else {
    //         setPaymentMethod("");
    //     }
    // };

    const handleSaveChange = async () => {

        // Validation for Residential
        if (selectedCategory === "RESIDENTIAL" && !residentialMatch && isSuperAdmin) {
            // If super admin is editing product details, warn if no match?
            // Or maybe allow saving anyway? 
            // Create form blocks it. Let's block it here too if they changed something.
            alert("Error: No standard package found for this panel quantity.");
            return;
        }


        const salesData = {
            "salesId": selectedSale.salesId,
            "updateSalesBySalesIdDto": {
                "agentId": selectedSale.agentDetails.userId,
                "coAgentId": selectedSale.coAgentDetails?.userId ?? null,
                "discountByAstern": asternDiscount || null,
                "discountByAgent": agentDiscount || null,
                "panelQty": panelQty,
                "energy": energy,
                "originalPrice": originalPrice,
                "finalPrice": finalPrice,
                "productCategory": selectedCategory,
                "paymentMethod": paymentMethod,
                "status": selectedSale.status,
                "depositOn": convertDateStringToDateTime(depositOn)
            },
            "updateCustomerBySalesId": {
                "customerName": customerName,
                "email": customerEmail,
                "phoneNumber": customerPhone,
                "address": customerAddress
            },
            "updatedBy": user.username
        };
        
        try {
            const updateSales = await updateSalesByAdmin(salesData);
            if (updateSales.status === 200) {
                try {
                    const logData = {
                        "message": "Sales changed: " + JSON.stringify(updateSales.data),
                        "type": "Update",
                        "createdBy": user.username
                    };

                    const logResponse = await createLog(logData);

                    if (logResponse.status === 200) {
                        console.log("Log created successfully");
                    }
                }
                catch (error) {
                    console.error("Failed to create log");
                }

                if (onSaved) {
                    onSaved();
                }

                onHide();
                alert("Sales updated successfully.");
            }
        } catch (error) {
            console.error("Failed to update sales: ", error);
        }
    };

    if (!show) {
        return null;
    }

    return (
        <Modal className="editSalesModal-modal" size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            show={show}
            onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Editing Sales Details For {selectedSale.customerDetails?.customerName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="editSalesForm-container">
                    <form className="createSalesForm-form">
                        <GenericSalesForm
                            mode="edit"
                            isSuperAdmin={isSuperAdmin}
                            canEditOriPrice={canEditOriPrice}
                            agentGroup={selectedSale.agentDetails?.groupName}
                            agentName={handleAgentName(selectedSale.agentDetails?.firstName, selectedSale.agentDetails?.lastName, selectedSale.agentDetails?.alias)}
                            coBrokeGroupOptions={[]} // Not editable in edit
                            coBrokeNameOptions={[]}   // Not editable in edit
                            selectedCoBrokeGroupId={selectedSale.coAgentDetails?.groupId}
                            setSelectedCoBrokeGroupId={() => { }}
                            selectedCoBrokeAgentId={selectedSale.coAgentDetails?.userId}
                            setSelectedCoBrokeAgentId={() => { }}
                            customerName={customerName}
                            setCustomerName={setCustomerName}
                            customerEmail={customerEmail}
                            setCustomerEmail={setCustomerEmail}
                            customerPhone={customerPhone}
                            setCustomerPhone={setCustomerPhone}
                            customerAddress={customerAddress}
                            setCustomerAddress={setCustomerAddress}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            panelQty={panelQty}
                            setPanelQty={setPanelQty}
                            energy={energy}
                            setEnergy={setEnergy}
                            originalPrice={originalPrice}
                            setOriginalPrice={setOriginalPrice}
                            residentialMatch={residentialMatch}
                            availablePaymentMethods={availablePaymentMethods}
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            canEditPrice={canEditPrice}
                            depositOn={depositOn}
                            setDepositOn={setDepositOn}
                            asternDiscount={asternDiscount}
                            setAsternDiscount={setAsternDiscount}
                            agentDiscount={agentDiscount}
                            setAgentDiscount={setAgentDiscount}
                            finalPrice={finalPrice}
                            setFinalPrice={setFinalPrice}
                            disableAgentFields={true}
                            disableCustomerFields={!isSuperAdmin}
                            disablePricingFields={false}
                            handleCoBrokeGroupChange={() => { }}
                        />
                    </form>
                </div >
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleSaveChange}>Save</Button>
            </Modal.Footer>
        </Modal >
    )
}